import { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Static step registry — steps are declared at module load time so they exist
// even before their component mounts. The useTourStep hook attaches a live ref
// when the component is in the DOM.
// ---------------------------------------------------------------------------

const staticSteps = new Map();

/**
 * Declare a tour step at module level (outside any component).
 * Call once per step — usually next to the component that owns it.
 *
 *   declareTourStep({
 *     id: 'bills-page',
 *     title: 'Bills Workspace',
 *     description: 'Your invoice pipeline.',
 *     priority: 40,
 *     path: '/dashboard/bills',
 *   });
 */
export const declareTourStep = ({ id, title, description, priority = 100, path }) => {
  staticSteps.set(id, { id, title, description, priority, path, ref: { current: null } });
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const TourContext = createContext({
  steps: [],
  start: () => {},
  stop: () => {},
  isOpen: false,
});

export const useTour = () => useContext(TourContext);

/**
 * Hook — attaches a ref to a previously declared step so the tour can
 * highlight the actual DOM element. Call inside the component that renders
 * the step's target element.
 *
 *   const ref = useTourStep('bills-page');
 *   return <div ref={ref}>...</div>;
 */
export const useTourStep = (stepId) => {
  const ref = useRef(null);
  const { attachRef, detachRef } = useContext(TourContext);

  useEffect(() => {
    attachRef(stepId, ref);
    return () => detachRef(stepId);
  }, [stepId, attachRef, detachRef]);

  return ref;
};

export const TourProvider = ({ children }) => {
  const [steps, setSteps] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const refsRef = useRef(new Map());

  // Build sorted step list from static registry + attached refs
  const rebuild = useCallback(() => {
    const sorted = [...staticSteps.values()]
      .sort((a, b) => a.priority - b.priority)
      .map(step => ({
        ...step,
        ref: refsRef.current.get(step.id) || step.ref,
      }));
    setSteps(sorted);
  }, []);

  // Rebuild once on mount to pick up all declareTourStep calls
  useEffect(() => { rebuild(); }, [rebuild]);

  const attachRef = useCallback((id, ref) => {
    refsRef.current.set(id, ref);
    rebuild();
  }, [rebuild]);

  const detachRef = useCallback((id) => {
    refsRef.current.delete(id);
    rebuild();
  }, [rebuild]);

  const start = useCallback(() => setIsOpen(true), []);
  const stop = useCallback(() => setIsOpen(false), []);

  return (
    <TourContext.Provider value={{ steps, start, stop, isOpen, attachRef, detachRef }}>
      {children}
    </TourContext.Provider>
  );
};
